import * as React from 'react';

import {useMountEffect} from './hooks/useMountEffect';
import {usePreviousValue} from './hooks/usePreviousValue';

const FocusTrap = ({
  active = true,
  children,
}: {
  active?: boolean;
  children: React.ReactNode;
}) => {
  const topId = React.useId();
  const bottomId = React.useId();

  const ref = React.useRef<HTMLDivElement | null>(null);
  const focused = React.useCallback(
    (event?: React.FocusEvent) => {
      const focusableNodes =
        ref.current?.querySelectorAll(
          '[role="button"], a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), [contenteditable="true"]',
        ) || [];
      if (event?.target.id === topId) {
        (focusableNodes[focusableNodes.length - 1] as HTMLElement).focus();
      } else {
        (focusableNodes[0] as HTMLElement)?.focus();
      }
      event?.preventDefault();
    },
    [topId],
  );

  useMountEffect(() => {
    if (active) {
      setTimeout(() => focused(), 100);
    }
  });

  const previousActive = usePreviousValue(active);
  React.useEffect(() => {
    if (active && !previousActive) {
      setTimeout(() => focused(), 100);
    }
  }, [active, focused, previousActive]);

  return (
    <React.Fragment>
      <div
        id={topId}
        style={{height: 0}}
        onFocus={focused}
        tabIndex={active ? 0 : undefined}
      />
      <div id="focus-trap" ref={ref}>
        {children}
      </div>
      <div
        id={bottomId}
        style={{height: 0}}
        onFocus={focused}
        tabIndex={active ? 0 : undefined}
      />
    </React.Fragment>
  );
};

export default FocusTrap;
